import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { moneyPageBySlug, relatedMoneyPages, siblingMoneyPages, moneyPlace, parseCityMoneySlug } from '@/data/seo/moneyPages';
import { STATES, statePath, licenseNote } from '@/data/seo/states';
import { breadcrumbSchema, faqSchema, serviceSchema, moneyFaqs, offerCatalogSchema, stateEntitySchema } from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money, PLAN_INCLUDED_LEADS } from '@/data/constants';

import LiveProjects from '@/components/seo/LiveProjects';
import MembershipOptions from '@/components/seo/MembershipOptions';
import CityMoneyPage from '@/pages/seo/CityMoneyPage';

const MoneyPage: React.FC = () => {
  const { slug = '' } = useParams();
  const { lang, path, t } = useI18n();
  const page = moneyPageBySlug(slug);
  if (!page) {
    // Not in the static catalog -> per-trade city page outside California
    // (/leads/flooring-leads-orlando-fl), driven by the cityIndex aggregate.
    const cityRoute = parseCityMoneySlug(slug);
    return cityRoute ? <CityMoneyPage key={slug} route={cityRoute} /> : <NotFound />;
  }

  const tradeName = lang === 'es' ? page.trade.es : page.trade.en;
  const place = moneyPlace(page, lang);
  const st = STATES.find((s) => s.code === page.stateCode);
  const isCa = page.stateCode === 'CA';
  const faqs = [
    ...moneyFaqs(page.trade.en, place, lang),
    // Statewide pages outside California carry the state's own licensing answer.
    ...(page.scope === 'state' && st && !isCa
      ? [{
          q: lang === 'es' ? `¿Necesito licencia para trabajos de ${tradeName.toLowerCase()} en ${place}?` : `Do I need a license for ${page.trade.en.toLowerCase()} work in ${place}?`,
          a: licenseNote(st, lang),
        }]
      : []),
  ];
  const url = `/${lang}/leads/${page.slug}`;
  const h1 = lang === 'es' ? `Leads de ${tradeName} en ${place}` : `${page.trade.en} Leads in ${place}`;

  const answer =
    lang === 'es'
      ? `Los leads de ${tradeName.toLowerCase()} en ${place} son proyectos reales publicados por propietarios y contratistas generales. Cada lead cuesta ${money(LEAD_PRICE)} e incluye alcance, presupuesto y, al desbloquearlo, la ciudad exacta, el código postal, los contactos aprobados y los planos adjuntos.`
      : `${page.trade.en} leads in ${place} are real projects posted by homeowners and general contractors. Each lead costs a flat ${money(LEAD_PRICE)} and includes the scope and budget range — unlocking it reveals the exact city, ZIP, approved contact details and any attached plan documents.`;

  const label = (m: typeof page) => (lang === 'es' ? `Leads de ${m.trade.es} en ${moneyPlace(m, lang)}` : `${m.trade.en} Leads ${m.place}`);
  const related = relatedMoneyPages(page).map((m) => ({ label: label(m), to: path(`/leads/${m.slug}`) }));
  const siblings = siblingMoneyPages(page, 14).map((m) => ({ label: label(m), to: path(`/leads/${m.slug}`) }));
  const marketplaceLink = `${path('/jobs')}?state=${page.stateCode}&trade=${page.trade.marketplaceTrade}`;
  const locationLinks = [
    ...(page.citySlug ? [{ label: `${page.place} construction projects`, to: path(`/california/${page.citySlug}`) }] : []),
    ...(page.countySlug ? [{ label: `${page.countySlug.replace(/-county$/, '').replace(/-/g, ' ')} County projects`, to: path(`/california/counties/${page.countySlug}`) }] : []),
    { label: lang === 'es' ? `Resumen del oficio: ${tradeName}` : `${page.trade.en} trade overview`, to: path(`/trades/${page.trade.slug}`) },
    ...(st ? [{ label: lang === 'es' ? `Proyectos de construcción en ${st.es}` : `${st.en} construction projects`, to: path(statePath(st)) }] : []),
    { label: lang === 'es' ? `Proyectos de ${tradeName.toLowerCase()} abiertos en ${place}` : `Open ${page.trade.en.toLowerCase()} projects in ${st?.en || place}`, to: marketplaceLink },
    ...(isCa ? [{ label: 'California construction hub', to: path('/california') }] : []),
  ];


  return (
    <>
      <Seo
        title={h1}
        description={
          lang === 'es'
            ? `Compre leads de ${tradeName.toLowerCase()} en ${place} por ${money(LEAD_PRICE)}. Proyectos verificados, precio fijo, sin contratos ocultos.`
            : `Buy ${page.trade.en.toLowerCase()} leads in ${place} for a flat ${money(LEAD_PRICE)}. Real posted projects, one price, no hidden bidding fees.`
        }
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: 'Construction Leads', url: `/${lang}/leads` },
            ...(page.scope === 'state' && st && !isCa ? [{ name: st.en, url: path(statePath(st)) }] : []),
            { name: h1, url },
          ]),
          serviceSchema({ trade: `${page.trade.en} leads`, area: page.place, description: answer, url }),
          ...(page.scope === 'state' && st ? [stateEntitySchema(st)] : []),
          offerCatalogSchema(),
          faqSchema(faqs),
        ]}
      />

      <PageHero eyebrow={t('seo.moneyEyebrow')} title={h1} intro={answer}>
        <Link to={path('/register')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaSubBtn')}
        </Link>
        <Link to={path('/pricing')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('nav.pricing')}
        </Link>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: t('seo.leadsHub'), url: path('/leads') },
            ...(page.scope === 'state' && st && !isCa ? [{ name: place, url: path(statePath(st)) }] : []),
            { name: h1 },
          ]}
        />

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />

        <EntityFacts
          rows={[
            { k: t('seo.factTrade'), v: tradeName },
            { k: t('seo.factArea'), v: place },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
            { k: t('seo.factIncluded'), v: `${PLAN_INCLUDED_LEADS.basic}–${PLAN_INCLUDED_LEADS.premium}` },
          ]}
        />

        <Section title={t('seo.whyBuy', { trade: tradeName, place })}>
          <p>{answer}</p>
          {page.scope === 'state' && st && !isCa && <p className="mt-3">{licenseNote(st, lang)}</p>}
          <TrustStrip />
        </Section>

        <MembershipOptions />

        {/* Live feed is filtered to THIS page's state (CA pages stay CA). */}
        <LiveProjects state={page.stateCode} trade={page.trade.marketplaceTrade} heading={t('seo.openProjects', { trade: tradeName })} />

        <FaqSection faqs={faqs} />

        <LinkGrid title={t('seo.sameTradeOther')} links={related} icon="map" />
        <LinkGrid title={t('seo.otherTradesPlace', { place })} links={siblings} icon="trade" />
        <LinkGrid title={t('seo.relatedPages')} links={locationLinks} columns={2} />


        <CtaBand />
      </PageWrap>
    </>
  );
};

export default MoneyPage;
